// src/features/executive/components/DrillTable/DrillTable.tsx
// Paginated table for drill-down bodies. Generic over row shape.
// No sorting/filtering in Phase 1E (deferred).

import { useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material'
import type { ReactNode } from 'react'
import EmptyState from 'features/executive/components/EmptyState/EmptyState'
import ErrorState from 'features/executive/components/ErrorState/ErrorState'
import LoadingSkeleton from 'features/executive/components/LoadingSkeleton/LoadingSkeleton'

export interface DrillTableColumn<T> {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  render: (row: T, index: number) => ReactNode
}

export interface DrillTableProps<T> {
  rows: T[]
  columns: DrillTableColumn<T>[]
  initialPageSize?: number
  loading?: boolean
  error?: Error | null
  onRetry?: () => void
}

export default function DrillTable<T>({
  rows,
  columns,
  initialPageSize = 10,
  loading = false,
  error = null,
  onRetry,
}: DrillTableProps<T>) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)

  if (loading) return <LoadingSkeleton height={240} />
  if (error) return <ErrorState onRetry={onRetry ?? (() => undefined)} />
  if (rows.length === 0) return <EmptyState />

  const start = page * pageSize
  const sliced = rows.slice(start, start + pageSize)

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align ?? 'left'}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sliced.map((row, idx) => (
              <TableRow key={idx} hover>
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align ?? 'left'}>
                    {c.render(row, start + idx)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(Number(e.target.value))
          setPage(0)
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Box>
  )
}
